import React, { useEffect, useMemo, useState } from "react";
import {
  copyStepConversationMessageToNotes,
  exportPlanDocx,
  getPlanSteps,
  getStepConversation,
  refineStepConversation,
  sendStepConversationMessage,
  Step,
  StepConversationMessage,
  StepConversationMode,
  updateStep,
} from "../api/client";

const AI_INPUT_MAX_CHARACTERS = 6000;
const AI_INPUT_WARNING_CHARACTERS = 4800;

type Props = {
  planId: number;
  planTitle: string;
  planDescription?: string;
  onBack: () => void;
  onCaptureSessionNote: (note: string) => Promise<void>;
  introduction?: boolean;
  onFinishIntroduction?: () => Promise<void>;
};

const starterPrompts: Record<Exclude<StepConversationMode, "conversation">, string> = {
  clarify: "Help me understand what this step means and why it matters.",
  options: "What practical options do I have for completing this step?",
  guide: "Guide me through how to begin this step in simple, practical actions.",
};

export default function ProjectWorkspace(props: Props) {
  const {
    planId,
    planTitle,
    planDescription,
    onBack,
    introduction = false,
    onFinishIntroduction,
  } = props;
  const [steps, setSteps] = useState<Step[]>([]);
  const [currentStepId, setCurrentStepId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [notesDraft, setNotesDraft] = useState("");
  const [instructionsDraft, setInstructionsDraft] = useState("");
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved">("idle");

  const [messages, setMessages] = useState<StepConversationMessage[]>([]);
  const [messageDraft, setMessageDraft] = useState("");
  const [conversationLoading, setConversationLoading] = useState(false);
  const [rookThinking, setRookThinking] = useState(false);
  const [conversationError, setConversationError] = useState<string | null>(null);
  const [copiedMessageId, setCopiedMessageId] = useState<number | null>(null);
  const [refining, setRefining] = useState(false);

  const [showExportOptions, setShowExportOptions] = useState(false);
  const [exportingDocx, setExportingDocx] = useState(false);
  const [exportMessage, setExportMessage] = useState<string | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const [finishingIntroduction, setFinishingIntroduction] = useState(false);
  const [exportOptions, setExportOptions] = useState({
    include_progress_summary: true,
    include_completed_steps: true,
    include_project_notes: true,
    include_next_actions: true,
    include_reflection_summary: false,
    include_reflection_prompts: false,
    include_display_name: false,
    include_export_date: true,
  });

  const currentStep = useMemo(
    () => steps.find((step) => step.id === currentStepId) ?? null,
    [steps, currentStepId]
  );
  const completedCount = steps.filter((step) => step.is_completed).length;

  useEffect(() => {
    let cancelled = false;

    async function loadSteps() {
      setLoading(true);
      setError(null);
      try {
        const data = await getPlanSteps(planId);
        if (cancelled) return;
        setSteps(data);
        const firstOpen = data.find((step) => !step.is_completed) ?? data[0];
        setCurrentStepId(firstOpen?.id ?? null);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to load this project");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadSteps();
    return () => {
      cancelled = true;
    };
  }, [planId]);

  useEffect(() => {
    setNotesDraft(currentStep?.notes ?? "");
    setInstructionsDraft(currentStep?.refined_output ?? "");
  }, [currentStepId, currentStep?.notes, currentStep?.refined_output]);

  useEffect(() => {
    if (!currentStepId) {
      setMessages([]);
      return;
    }

    let cancelled = false;
    setConversationLoading(true);
    setConversationError(null);
    setCopiedMessageId(null);

    getStepConversation(currentStepId)
      .then((data) => {
        if (!cancelled) setMessages(data);
      })
      .catch((err) => {
        if (!cancelled) {
          setConversationError(
            err instanceof Error ? err.message : "Failed to load your conversation"
          );
        }
      })
      .finally(() => {
        if (!cancelled) setConversationLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [currentStepId]);

  function replaceStep(updated: Step) {
    setSteps((previous) =>
      previous.map((step) => (step.id === updated.id ? updated : step))
    );
  }

  async function saveStepWork() {
    if (!currentStep) return;
    setSaveStatus("saving");
    setError(null);
    try {
      const updated = await updateStep(currentStep.id, {
        notes: notesDraft,
        refined_output: instructionsDraft,
      });
      replaceStep(updated);
      setSaveStatus("saved");
      window.setTimeout(() => setSaveStatus("idle"), 1800);
    } catch (err) {
      setSaveStatus("idle");
      setError(err instanceof Error ? err.message : "Failed to save this step");
      throw err;
    }
  }

  async function sendToRook(
    mode: StepConversationMode = "conversation",
    prompt?: string
  ) {
    if (!currentStep || rookThinking) return;
    const content = (prompt ?? messageDraft).trim();
    if (!content) return;

    if (content.length > AI_INPUT_MAX_CHARACTERS) {
      setConversationError(
        "Please shorten your question to 6,000 characters before sending it."
      );
      return;
    }

    setRookThinking(true);
    setConversationError(null);
    if (!prompt) setMessageDraft("");
    try {
      const reply = await sendStepConversationMessage(currentStep.id, {
        content,
        mode,
      });
      setMessages((previous) => [
        ...previous,
        reply.user_message,
        reply.assistant_message,
      ]);
    } catch (err) {
      setConversationError(
        err instanceof Error ? err.message : "Rook could not respond just now"
      );
    } finally {
      setRookThinking(false);
    }
  }

  async function copyToNotes(message: StepConversationMessage) {
    if (!currentStep) return;
    setConversationError(null);
    try {
      const updated = await copyStepConversationMessageToNotes(
        currentStep.id,
        message.id
      );
      replaceStep(updated);
      setNotesDraft(updated.notes ?? "");
      setCopiedMessageId(message.id);
    } catch (err) {
      setConversationError(
        err instanceof Error ? err.message : "Failed to copy this response"
      );
    }
  }

  async function createInstructions() {
    if (!currentStep) return;
    setRefining(true);
    setConversationError(null);
    try {
      const result = await refineStepConversation(currentStep.id);
      setInstructionsDraft(result.refined_output);
      setSteps((previous) =>
        previous.map((step) =>
          step.id === currentStep.id
            ? { ...step, refined_output: result.refined_output }
            : step
        )
      );
    } catch (err) {
      setConversationError(
        err instanceof Error ? err.message : "Failed to create instructions"
      );
    } finally {
      setRefining(false);
    }
  }

  async function moveToNextStep() {
    if (!currentStep) return;
    setSaveStatus("saving");
    try {
      const saved = await updateStep(currentStep.id, {
        notes: notesDraft,
        refined_output: instructionsDraft,
        is_completed: true,
      });
      const updatedSteps = steps.map((step) =>
        step.id === saved.id ? saved : step
      );
      setSteps(updatedSteps);
      const next = updatedSteps.find((step) => !step.is_completed);
      if (next) setCurrentStepId(next.id);
      setSaveStatus("saved");
    } catch (err) {
      setSaveStatus("idle");
      setError(err instanceof Error ? err.message : "Failed to complete this step");
    }
  }

  async function reopenStep() {
    if (!currentStep) return;
    try {
      const updated = await updateStep(currentStep.id, { is_completed: false });
      replaceStep(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to reopen this step");
    }
  }

  async function handleExportDocx() {
    setExportingDocx(true);
    setExportMessage(null);
    setExportError(null);
    try {
      await saveStepWork();
      await exportPlanDocx(planId, {
        ...exportOptions,
        include_ai_support_notice: true,
      });
      setExportMessage("Your project document has downloaded.");
    } catch (err) {
      setExportError(err instanceof Error ? err.message : "Failed to export document");
    } finally {
      setExportingDocx(false);
    }
  }

  async function selectStep(step: Step) {
    if (
      currentStep &&
      (notesDraft !== (currentStep.notes ?? "") ||
        instructionsDraft !== (currentStep.refined_output ?? ""))
    ) {
      try {
        await saveStepWork();
      } catch {
        return;
      }
    }
    setCurrentStepId(step.id);
    setMessageDraft("");
    setError(null);
  }

  async function handleBack() {
    if (
      currentStep &&
      (notesDraft !== (currentStep.notes ?? "") ||
        instructionsDraft !== (currentStep.refined_output ?? ""))
    ) {
      try {
        await saveStepWork();
      } catch {
        return;
      }
    }
    onBack();
  }

  async function finishIntroduction() {
    if (!onFinishIntroduction || finishingIntroduction) return;
    setFinishingIntroduction(true);
    setError(null);
    try {
      await onFinishIntroduction();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not finish the introduction");
      setFinishingIntroduction(false);
    }
  }

  return (
    <section className="project-workspace-page">
      {introduction ? (
        <div className="project-introduction-guide">
          <p className="onboarding-kicker">YOUR PROJECT IS READY</p>
          <h2>This is where you turn a plan into progress</h2>
          <div className="project-introduction-guide__grid">
            <article><strong>1. Choose a step</strong><span>Open any step. You do not have to follow the list blindly.</span></article>
            <article><strong>2. Ask Rook</strong><span>Ask until you understand what to do and how to do it.</span></article>
            <article><strong>3. Keep what helps</strong><span>Copy useful replies to notes or create clear editable instructions.</span></article>
            <article><strong>4. Continue your way</strong><span>Complete, reopen, pause, create a supporting project, or return later.</span></article>
          </div>
          <p>
            Your conversation is saved with the selected step. Your notes and instructions can be included when you download the project document.
          </p>
          <div className="project-introduction-guide__actions">
            <a href="/sanctuary-getting-started-workbook.pdf" download>Download printable workbook</a>
            <a href="/sanctuary-getting-started-workbook.docx" download>Download editable workbook</a>
            <button type="button" onClick={finishIntroduction} disabled={finishingIntroduction}>
              {finishingIntroduction ? "Saving…" : "I understand — start my project"}
            </button>
          </div>
        </div>
      ) : null}

      <div className="project-workspace-toolbar">
        <button type="button" onClick={handleBack}>← Back to projects</button>
        <button
          type="button"
          onClick={() => setShowExportOptions((shown) => !shown)}
        >
          {showExportOptions ? "Hide document options" : "Download project document"}
        </button>
      </div>

      {showExportOptions ? (
        <div className="project-export-panel">
          <strong>Choose what to include</strong>
          {[
            ["include_progress_summary", "Progress summary"],
            ["include_completed_steps", "Completed steps"],
            ["include_project_notes", "Your notes and instructions"],
            ["include_next_actions", "Next actions"],
            ["include_export_date", "Export date"],
          ].map(([key, label]) => (
            <label key={key}>
              <input
                type="checkbox"
                checked={Boolean(exportOptions[key as keyof typeof exportOptions])}
                onChange={(event) =>
                  setExportOptions((previous) => ({
                    ...previous,
                    [key]: event.target.checked,
                  }))
                }
              />{" "}{label}
            </label>
          ))}
          <button type="button" onClick={handleExportDocx} disabled={exportingDocx}>
            {exportingDocx ? "Preparing document..." : "Download DOCX"}
          </button>
        </div>
      ) : null}

      {exportMessage ? <p className="workspace-success">{exportMessage}</p> : null}
      {exportError ? <p className="workspace-error">{exportError}</p> : null}

      <h1>{planTitle}</h1>
      {planDescription ? <p className="project-workspace-description">{planDescription}</p> : null}
      <p className="project-workspace-progress">
        {completedCount} of {steps.length} steps complete. You can open any step at any time.
      </p>

      {loading ? <p>Loading project...</p> : null}
      {error ? <p className="workspace-error">{error}</p> : null}

      {!loading ? (
        <div className="project-workspace-grid">
          <aside className="project-workspace-panel project-step-list">
            <h2>Project steps</h2>
            <p>Choose the step that is useful to you now. You remain in control.</p>
            {steps.map((step, index) => (
              <button
                key={step.id}
                type="button"
                className={currentStepId === step.id ? "project-step-button project-step-button--active" : "project-step-button"}
                onClick={() => void selectStep(step)}
              >
                <strong>{index + 1}. {step.title}</strong>
                <span>{step.is_completed ? "✓ Complete" : "Open"}</span>
              </button>
            ))}
          </aside>

          <main className="project-workspace-panel project-step-work">
            {currentStep ? (
              <>
                <h2>{currentStep.title}</h2>
                {currentStep.description ? <p>{currentStep.description}</p> : null}

                <label className="project-field-label" htmlFor="step-notes">
                  Your working notes
                </label>
                <p className="project-field-help">
                  Keep research, decisions, useful wording, links, and anything you want to remember.
                </p>
                <textarea
                  id="step-notes"
                  value={notesDraft}
                  onChange={(event) => setNotesDraft(event.target.value)}
                  rows={8}
                  placeholder="Add your notes here..."
                />

                <label className="project-field-label" htmlFor="step-instructions">
                  Your clear instructions
                </label>
                <p className="project-field-help">
                  After talking with Rook, turn the useful discussion into an editable practical guide.
                </p>
                <textarea
                  id="step-instructions"
                  value={instructionsDraft}
                  onChange={(event) => setInstructionsDraft(event.target.value)}
                  rows={8}
                  placeholder="Your practical step-by-step instructions will appear here..."
                />

                {messages.some((message) => message.role === "assistant") ? (
                  <button type="button" onClick={createInstructions} disabled={refining}>
                    {refining ? "Creating instructions..." : "Create clear instructions from this conversation"}
                  </button>
                ) : null}

                <div className="project-step-actions">
                  <button type="button" onClick={saveStepWork}>Save this step</button>
                  {currentStep.is_completed ? (
                    <button type="button" onClick={reopenStep}>Reopen step</button>
                  ) : (
                    <button type="button" onClick={moveToNextStep}>Complete and continue →</button>
                  )}
                  {saveStatus === "saving" ? <span>Saving...</span> : null}
                  {saveStatus === "saved" ? <span className="workspace-success">✓ Saved</span> : null}
                </div>
              </>
            ) : <p>This project does not have any steps yet.</p>}
          </main>

          <aside className="project-workspace-panel project-rook-panel">
            <h2>Ask Rook</h2>
            <p>
              Ask as many questions as you need. This conversation is saved with this step, so you can leave and return to it.
            </p>

            {conversationLoading ? <p>Loading your conversation...</p> : null}
            {!conversationLoading && messages.length === 0 ? (
              <div className="rook-starter-actions">
                <button type="button" onClick={() => sendToRook("clarify", starterPrompts.clarify)}>Help me understand</button>
                <button type="button" onClick={() => sendToRook("options", starterPrompts.options)}>Give me options</button>
                <button type="button" onClick={() => sendToRook("guide", starterPrompts.guide)}>Guide me step by step</button>
              </div>
            ) : null}

            <div className="rook-conversation" aria-live="polite">
              {messages.map((message) => (
                <article
                  key={message.id}
                  className={message.role === "assistant" ? "rook-message rook-message--assistant" : "rook-message rook-message--user"}
                >
                  <strong>{message.role === "assistant" ? "Rook" : "You"}</strong>
                  <div>{message.content}</div>
                  {message.role === "assistant" ? (
                    <button type="button" onClick={() => copyToNotes(message)}>
                      {copiedMessageId === message.id ? "✓ Copied to notes" : "Copy to step notes"}
                    </button>
                  ) : null}
                </article>
              ))}
              {rookThinking ? <p>Rook is thinking this through...</p> : null}
            </div>

            <label className="project-field-label" htmlFor="rook-question">Your question</label>
            <textarea
              id="rook-question"
              value={messageDraft}
              onChange={(event) => setMessageDraft(event.target.value)}
              maxLength={AI_INPUT_MAX_CHARACTERS}
              rows={4}
              placeholder="Ask what this means, how to do it, or for the next smaller action..."
              onKeyDown={(event) => {
                if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
                  event.preventDefault();
                  sendToRook();
                }
              }}
            />
            {messageDraft.length >= AI_INPUT_WARNING_CHARACTERS ? (
              <p
                aria-live="polite"
                style={{
                  margin: "0.4rem 0 0.75rem",
                  fontSize: "0.9rem",
                  opacity: 0.8,
                  color:
                    messageDraft.length >= AI_INPUT_MAX_CHARACTERS
                      ? "#f59e0b"
                      : "inherit",
                }}
              >
                {messageDraft.length >= AI_INPUT_MAX_CHARACTERS
                  ? "You have reached the 6,000-character limit."
                  : `You have ${(
                      AI_INPUT_MAX_CHARACTERS - messageDraft.length
                    ).toLocaleString()} characters left.`}
              </p>
            ) : null}
            <button
              type="button"
              onClick={() => sendToRook()}
              disabled={!messageDraft.trim() || rookThinking}
            >
              Ask Rook
            </button>

            {conversationError ? <p className="workspace-error">{conversationError}</p> : null}
          </aside>
        </div>
      ) : null}
    </section>
  );
}
